import { PluginInterface } from '../../types/PluginInterface';
import { EventBookingConfig } from './types';
import { defaultConfig } from './config';
import { EventBookingConfigComponent } from './EventBookingConfigComponent';
import { EventBookingRenderer } from './EventBookingRenderer';
import { generateUniformPlaceholder } from '../../utils/PlaceholderGenerator';
import { ALL_COUNTRIES } from '../../utils/countries';

const resolveOwnerId = (config: EventBookingConfig): string => {
  const candidates = [
    (config as any)?.userId,
    (config as any)?.ownerId,
    (config as any)?.createdBy,
    (config as any)?.created_by,
    (config as any)?.authorId,
    (config as any)?.user?.id,
    (config as any)?.user?.userId
  ];
  for (const candidate of candidates) {
    if (candidate === undefined || candidate === null) continue;
    const str = String(candidate).trim();
    if (str) return str;
  }
  return '';
};

const normalizeVideoUrl = (raw?: string | null): string | null => {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getVideoEmbedUrl = (rawUrl?: string | null): string | null => {
  if (!rawUrl) return null;
  try {
    const url = new URL(rawUrl);
    const host = url.hostname.toLowerCase();
    if (host.includes('youtube.com')) {
      if (url.pathname.startsWith('/shorts/')) {
        const id = url.pathname.split('/')[2];
        return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
      }
      const v = url.searchParams.get('v');
      if (v) return `https://www.youtube-nocookie.com/embed/${v}`;
      if (url.pathname.includes('/embed/')) return rawUrl;
    }
    if (host.includes('youtu.be')) {
      const id = url.pathname.replace('/', '');
      return id ? `https://www.youtube-nocookie.com/embed/${id}` : null;
    }
    if (host.includes('vimeo.com')) {
      const id = url.pathname.split('/').filter(Boolean)[0];
      return id ? `https://player.vimeo.com/video/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
};

function buildPublicWidget(config: EventBookingConfig): string {
  if (!config.slug || !config.title) {
    // Si pas prêt, on retourne le placeholder standard
    return String(generateUniformPlaceholder({
      pluginId: config.id || 'event-booking-incomplete',
      pluginType: 'event-booking',
      category: 'commerce',
      title: 'Réservation Événement (Incomplet)',
      description: 'Configuration requise',
      icon: '🎟️',
      config
    }));
  }

  const safeSlug = config.slug.replace(/[^a-z0-9_-]/gi, '_');
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
  const ownerId = resolveOwnerId(config);
  const videoUrl = normalizeVideoUrl(config.videoUrl);
  const videoEmbedUrl = getVideoEmbedUrl(videoUrl);
  const addressParts = [config.addressLine1, config.city, config.postalCode, config.country].filter(Boolean);
  const mapEnabled = config.enableGeolocation !== false && addressParts.length > 0;
  const mapQuery = mapEnabled ? encodeURIComponent(addressParts.join(', ')) : '';
  const ticketCategories = Array.isArray(config.ticketCategories) ? config.ticketCategories : [];
  const displayPriceCents = ticketCategories.length
    ? Math.min(...ticketCategories.map((cat) => Number(cat.priceCents || 0)))
    : Number(config.priceCents || 0);
  const pricePrefix = ticketCategories.length ? 'À partir de ' : '';
  const defaultCountry = config.country || 'France';
  const countryOptions = ALL_COUNTRIES.map((country) =>
    `<option value="${country.label}">${country.flag} ${country.label}</option>`
  ).join('');

  return `
<div class="event-booking-widget" data-plugin="event-booking" data-slug="${config.slug}" data-owner-id="${ownerId}" style="
  max-width: 900px;
  margin: 30px auto;
  padding: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(102, 126, 234, 0.4);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: white;
">
  <style>
    .event-booking-public-grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
    .event-booking-public-grid > * { min-width: 0; }
    .event-booking-public-payment { position: sticky; top: 16px; align-self: start; }
    .event-booking-slot { transition: all 0.2s; }
    .event-booking-slot:not([disabled]):hover { background: #eef2ff !important; }
    @media (min-width: 1200px) { .event-booking-public-grid { grid-template-columns: 1.2fr 0.8fr; } }
    @media (max-width: 1200px) { .event-booking-public-payment { position: static; } }
    @media (max-width: 640px) {
      .event-booking-widget { padding: 18px; }
    }
  </style>
  ${config.imageUrl ? `
    <img src="${config.imageUrl}" alt="${config.title}" style="
      width: 100%;
      height: 250px;
      object-fit: cover;
      border-radius: 15px;
      margin-bottom: 25px;
      box-shadow: 0 5px 20px rgba(0,0,0,0.3);
    " />
  ` : ''}

  <h1 style="margin: 0 0 10px 0; font-size: 36px; font-weight: 800; letter-spacing: -1px;">
    ${config.title}
  </h1>

  ${config.description ? `
    <p style="margin: 0 0 20px 0; font-size: 16px; line-height: 1.6; opacity: 0.95;">
      ${config.description}
    </p>
  ` : ''}

  ${videoUrl ? `
    <div style="background: white; border-radius: 12px; overflow: hidden; margin-bottom: 20px; color: #1e293b; box-shadow: 0 5px 20px rgba(0,0,0,0.2);">
      <div style="padding: 12px 16px; font-weight: 700; border-bottom: 1px solid #e2e8f0;">🎬 Vidéo</div>
      <div style="position: relative; width: 100%; padding-top: 56.25%; background: #000;">
        ${videoEmbedUrl ? `
          <iframe
            title="Vidéo"
            src="${videoEmbedUrl}"
            style="position:absolute; inset:0; width:100%; height:100%; border:0; display:block;"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        ` : `
          <video src="${videoUrl}" controls style="position:absolute; inset:0; width:100%; height:100%; display:block;"></video>
        `}
      </div>
      ${!videoEmbedUrl ? `
        <div style="padding: 10px 16px; font-size: 12px;">
          <a href="${videoUrl}" target="_blank" rel="noreferrer" style="font-weight: 600;">Ouvrir la vidéo ↗</a>
        </div>
      ` : ''}
    </div>
  ` : ''}

  <div style="
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(10px);
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 25px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  ">
    <div>
      <div style="font-size: 14px; opacity: 0.8; margin-bottom: 5px;">Prix par personne</div>
      <div style="font-size: 34px; font-weight: 700;">
        ${pricePrefix}${(displayPriceCents / 100).toFixed(2)} ${config.currency}
      </div>
    </div>
    ${displayPriceCents === 0 ? `
      <div style="
        background: rgba(34, 197, 94, 0.3);
        color: #fff;
        padding: 10px 20px;
        border-radius: 25px;
        font-weight: 700;
        font-size: 14px;
      ">
        GRATUIT
      </div>
    ` : ''}
  </div>

  <div id="calendar-${safeSlug}" style="
    background: white;
    border-radius: 15px;
    padding: 25px;
    color: #1e293b;
    min-height: 300px;
    box-shadow: 0 5px 20px rgba(0,0,0,0.2);
  ">
    <div style="text-align: center; padding: 50px 0; color: #94a3b8;">
      <div style="font-size: 48px; margin-bottom: 15px;">📅</div>
      <div style="font-size: 16px; font-weight: 600;">Chargement des créneaux...</div>
    </div>
  </div>

  ${mapEnabled ? `
    <div style="background: white; border-radius: 12px; padding: 0; margin-top: 20px; color: #1e293b; box-shadow: 0 5px 20px rgba(0,0,0,0.2); overflow: hidden;">
      <div style="padding: 12px 16px; font-weight: 700; border-bottom: 1px solid #e2e8f0;">📍 Géolocalisation</div>
      <iframe
        title="Géolocalisation"
        src="https://www.google.com/maps?q=${mapQuery}&output=embed"
        style="width: 100%; height: 280px; border: 0; display: block;"
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
      ></iframe>
      <div style="padding: 10px 16px; border-top: 1px solid #e2e8f0; display:flex; justify-content: space-between; align-items:center;">
        <div style="font-size: 12px; color: #475569;">${addressParts.join(', ')}</div>
        <a href="https://www.google.com/maps/search/?api=1&query=${mapQuery}" target="_blank" rel="noreferrer" style="font-weight:700">Ouvrir ↗</a>
      </div>
    </div>
  ` : ''}

  <div id="booking-form-${safeSlug}" style="
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 15px;
    padding: 25px;
    margin-top: 25px;
    display: none;
  ">
    <h3 style="margin: 0 0 20px 0; font-size: 22px; font-weight: 700;">Réserver ce créneau</h3>
    <div class="event-booking-public-grid">
      <div style="display: grid; gap: 15px;">
        <div style="display:grid; gap:12px; grid-template-columns:repeat(auto-fit,minmax(160px,1fr));">
          <input type="text" id="first-name-${safeSlug}" placeholder="Prénom" style="
            padding: 12px;
            border: none;
            border-radius: 10px;
            font-size: 14px;
            background: rgba(255,255,255,0.9);
            color: #1e293b;
          " required />
          <input type="text" id="last-name-${safeSlug}" placeholder="Nom" style="
            padding: 12px;
            border: none;
            border-radius: 10px;
            font-size: 14px;
            background: rgba(255,255,255,0.9);
            color: #1e293b;
          " required />
        </div>
        <input type="email" id="email-${safeSlug}" placeholder="Votre email" style="
          padding: 12px;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          background: rgba(255,255,255,0.9);
          color: #1e293b;
        " required />
        <input type="tel" id="phone-${safeSlug}" placeholder="Téléphone" style="
          padding: 12px;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          background: rgba(255,255,255,0.9);
          color: #1e293b;
        " required />
        <input type="text" id="address-${safeSlug}" placeholder="Adresse" style="
          padding: 12px;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          background: rgba(255,255,255,0.9);
          color: #1e293b;
        " required />
        <div style="display:grid; gap:12px; grid-template-columns:repeat(auto-fit,minmax(160px,1fr));">
          <input type="text" id="city-${safeSlug}" placeholder="Ville" style="
            padding: 12px;
            border: none;
            border-radius: 10px;
            font-size: 14px;
            background: rgba(255,255,255,0.9);
            color: #1e293b;
          " required />
          <input type="text" id="postal-${safeSlug}" placeholder="Code postal" style="
            padding: 12px;
            border: none;
            border-radius: 10px;
            font-size: 14px;
            background: rgba(255,255,255,0.9);
            color: #1e293b;
          " required />
        </div>
        <select id="country-${safeSlug}" style="
          padding: 12px;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          background: rgba(255,255,255,0.9);
          color: #1e293b;
        " required>
          <option value="">Sélectionner un pays...</option>
          ${countryOptions}
        </select>
        <div id="ticket-category-wrap-${safeSlug}" style="display:none;">
          <select id="ticket-category-${safeSlug}" style="
            padding: 12px;
            border: none;
            border-radius: 10px;
            font-size: 14px;
            background: rgba(255,255,255,0.9);
            color: #1e293b;
            width: 100%;
          "></select>
          <div id="ticket-category-availability-${safeSlug}" style="font-size:12px;color:#475569;margin-top:6px;"></div>
        </div>
        <input type="text" id="coupon-${safeSlug}" placeholder="Code promo (optionnel)" style="
          padding: 12px;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          background: rgba(255,255,255,0.9);
          color: #1e293b;
        " />
        <input type="number" id="qty-${safeSlug}" placeholder="Nombre de places" min="1" value="1" style="
          padding: 12px;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          background: rgba(255,255,255,0.9);
          color: #1e293b;
        " />
        <button id="cancel-booking-${safeSlug}" style="
          padding: 12px;
          background: rgba(255,255,255,0.2);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
        " type="button">
          Annuler
        </button>
      </div>
      <div class="event-booking-public-payment" style="display: grid; gap: 12px;">
        <div id="waitlist-box-${safeSlug}" style="display:none; background: rgba(59,130,246,0.15); color: #fff; padding: 12px; border-radius: 10px; font-size: 13px;">
          <div style="font-weight:700; margin-bottom:6px;">Créneau complet</div>
          <div style="font-size:12px; margin-bottom:10px;">Rejoignez la liste d’attente pour être notifié si une place se libère.</div>
          <button id="confirm-waitlist-${safeSlug}" style="
            padding: 12px;
            background: #0ea5e9;
            color: white;
            border: none;
            border-radius: 10px;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            width: 100%;
          ">Rejoindre la liste d’attente</button>
        </div>
        <div id="payment-warning-${safeSlug}" style="display:none; background: rgba(239,68,68,0.15); color: #fff; padding: 10px 12px; border-radius: 10px; font-size: 13px;"></div>
        <div id="payment-total-${safeSlug}" style="display:none; background: rgba(15,23,42,0.2); color: #fff; padding: 10px 12px; border-radius: 10px; font-size: 13px;"></div>
        <div id="payment-methods-${safeSlug}" style="display:none; gap:10px;">
          <button id="confirm-free-${safeSlug}" style="
            padding: 14px;
            background: #10b981;
            color: white;
            border: none;
            border-radius: 10px;
            font-size: 15px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.3s;
          ">Confirmer gratuitement</button>
          <button id="confirm-stripe-${safeSlug}" style="
            padding: 14px;
            background: #1e40af;
            color: white;
            border: none;
            border-radius: 10px;
            font-size: 15px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.3s;
          ">Payer par carte (Stripe Checkout)</button>
          <div id="paypal-button-container-${safeSlug}" style="display:none;"></div>
        </div>
      </div>
    </div>
  </div>
</div>

<script>
(function() {
  const API_BASE = ${JSON.stringify(API_URL)};
  const base = API_BASE.replace(/\\/+$/,'');
  const API = /\\/api$/i.test(base) ? base : base + '/api';
  const slug = ${JSON.stringify(config.slug)};
  const safeSlug = ${JSON.stringify(safeSlug)};
  let ownerId = ${JSON.stringify(ownerId)};
  const priceCents = ${Number(config.priceCents || 0)};
  const currency = ${JSON.stringify(config.currency || 'EUR')};
  const defaultCountry = ${JSON.stringify(defaultCountry)};
  let ticketCategories = ${JSON.stringify(config.ticketCategories || [])};
  const coupons = ${JSON.stringify(config.coupons || [])};
  const mapEnabled = ${mapEnabled ? 'true' : 'false'};
  const mapQuery = ${JSON.stringify(mapQuery)};
  let selectedOccurrence = null;
  let paymentKeys = { stripePublicKey: null, paypalClientId: null };
  let paymentKeysLoaded = false;

  async function ensureOwnerId() {
    if (ownerId) return ownerId;
    try {
      const res = await fetch(API + '/events/' + encodeURIComponent(slug));
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      if (data && data.event && data.event.ownerId) {
        ownerId = String(data.event.ownerId);
      }
    } catch (err) {
      console.warn('OwnerId introuvable', err);
    }
    return ownerId || '';
  }

  const cancelBtn = document.getElementById('cancel-booking-' + safeSlug);
  if (cancelBtn) {
    cancelBtn.addEventListener('click', function() {
      const form = document.getElementById('booking-form-' + safeSlug);
      if (form) form.style.display = 'none';
    });
  }

  function showWarning(message) {
    const warning = document.getElementById('payment-warning-' + safeSlug);
    if (!warning) return;
    warning.textContent = message || '';
    warning.style.display = message ? 'block' : 'none';
  }

  function clearWarning() {
    showWarning('');
  }

  function normalizeCouponCode(value) {
    return value ? String(value).trim().toUpperCase() : '';
  }

  function resolveCoupon(code) {
    const normalized = normalizeCouponCode(code);
    if (!normalized) return null;
    const match = (coupons || []).find(function(coupon) {
      return normalizeCouponCode(coupon.code) === normalized && coupon.active !== false;
    });
    if (!match) return null;
    if (match.maxUses !== undefined && match.maxUses !== null && match.usedCount !== undefined) {
      if (match.usedCount >= match.maxUses) return null;
    }
    return match;
  }

  function computeDiscountCents(baseAmount, coupon) {
    if (!coupon || baseAmount <= 0) return 0;
    if (coupon.type === 'PERCENT') {
      const percent = Math.max(0, Math.min(100, Number(coupon.percentOff || 0)));
      return Math.min(baseAmount, Math.round(baseAmount * (percent / 100)));
    }
    const fixed = Math.max(0, Number(coupon.amountCents || 0));
    return Math.min(baseAmount, fixed);
  }

  function getSelectedCategory() {
    if (!ticketCategories || ticketCategories.length === 0) return null;
    const select = document.getElementById('ticket-category-' + safeSlug);
    const current = select && select.value ? String(select.value) : String(ticketCategories[0].id);
    return ticketCategories.find(function(cat) { return String(cat.id) === current; }) || ticketCategories[0];
  }

  function populateTicketCategories() {
    const wrap = document.getElementById('ticket-category-wrap-' + safeSlug);
    const select = document.getElementById('ticket-category-' + safeSlug);
    if (!wrap || !select) return;
    if (!ticketCategories || ticketCategories.length === 0) {
      wrap.style.display = 'none';
      return;
    }
    wrap.style.display = 'block';
    select.innerHTML = '';
    ticketCategories.forEach(function(cat) {
      const option = document.createElement('option');
      option.value = String(cat.id);
      option.textContent = cat.name + ' • ' + ((cat.priceCents || 0) / 100).toFixed(2) + ' ' + currency;
      select.appendChild(option);
    });
  }

  function getSelectedCategoryAvailability() {
    if (!selectedOccurrence) return null;
    const category = getSelectedCategory();
    if (category && selectedOccurrence.availableByCategory) {
      const value = selectedOccurrence.availableByCategory[String(category.id)];
      return typeof value === 'number' ? value : null;
    }
    return typeof selectedOccurrence.available === 'number' ? selectedOccurrence.available : null;
  }

  function computeTotals() {
    const qtyEl = document.getElementById('qty-' + safeSlug);
    const qty = qtyEl && qtyEl.value ? parseInt(qtyEl.value, 10) || 1 : 1;
    const category = getSelectedCategory();
    const unitPriceCents = category ? Number(category.priceCents || 0) : priceCents;
    const baseAmount = Math.max(0, qty * unitPriceCents);
    const couponInput = document.getElementById('coupon-' + safeSlug);
    const couponCode = couponInput && couponInput.value ? couponInput.value : '';
    const coupon = resolveCoupon(couponCode);
    const discountCents = computeDiscountCents(baseAmount, coupon);
    const totalCents = Math.max(0, baseAmount - discountCents);
    return { qty: qty, unitPriceCents: unitPriceCents, totalCents: totalCents, discountCents: discountCents, coupon: coupon };
  }

  function updatePriceSummary() {
    const totalEl = document.getElementById('payment-total-' + safeSlug);
    if (!totalEl) return;
    const totals = computeTotals();
    totalEl.style.display = 'block';
    const totalLabel = (totals.totalCents / 100).toFixed(2) + ' ' + currency;
    const discountLabel = totals.discountCents > 0
      ? ' (Réduction: -' + (totals.discountCents / 100).toFixed(2) + ' ' + currency + ')'
      : '';
    totalEl.textContent = 'Total : ' + totalLabel + discountLabel;
  }

  function updateCategoryAvailabilityLabel() {
    const label = document.getElementById('ticket-category-availability-' + safeSlug);
    if (!label) return;
    const availability = getSelectedCategoryAvailability();
    if (availability == null) {
      label.textContent = '';
      return;
    }
    label.textContent = 'Disponibles : ' + availability;
  }

  async function loadCalendar() {
    const container = document.getElementById('calendar-' + safeSlug);
    if (!container) return;

    try {
      const res = await fetch(API + '/events/' + encodeURIComponent(slug) + '/available-slots');
      if (!res.ok) throw new Error('HTTP ' + res.status);

      const data = await res.json();
      if (!data.success || !data.event) {
        container.innerHTML = '<div style="text-align:center;padding:50px;color:#ef4444"><div style="font-size:48px">⚠️</div><div style="font-weight:600">Événement introuvable</div></div>';
        return;
      }

      if (Array.isArray(data.event.ticketCategories)) {
        ticketCategories = data.event.ticketCategories;
        populateTicketCategories();
      } else {
        populateTicketCategories();
      }
      const occs = data.event.occurrences || [];
      if (occs.length === 0) {
        container.innerHTML = '<div style="text-align:center;padding:50px;color:#64748b"><div style="font-size:48px">📅</div><div style="font-weight:600">Aucune date disponible</div></div>';
        return;
      }

      // Grouper par date
      const byDate = {};
      occs.forEach(function(occ) {
        const dateKey = new Date(occ.startsAt).toLocaleDateString('fr-FR');
        if (!byDate[dateKey]) byDate[dateKey] = [];
        byDate[dateKey].push(occ);
      });

      let html = '<div style="display:grid;gap:20px">';
      Object.keys(byDate).sort().forEach(function(dateKey) {
        html += '<div style="border:2px solid #e2e8f0;border-radius:12px;padding:20px;background:#f8fafc">';
        html += '<h4 style="margin:0 0 15px 0;font-size:18px;font-weight:700;color:#1e293b">📅 ' + dateKey + '</h4>';
        html += '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:10px">';

        byDate[dateKey].forEach(function(occ) {
          const start = new Date(occ.startsAt);
          const end = new Date(occ.endsAt);
          const time = start.toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'}) + ' - ' + end.toLocaleTimeString('fr-FR', {hour:'2-digit',minute:'2-digit'});
          const isBlocked = !!occ.isBlocked;
          const isFull = !isBlocked && occ.available <= 0;
          const disabled = isBlocked;
          const availableByCategory = JSON.stringify(occ.availableByCategory || {}).replace(/'/g, "\\'");

          html += '<button class="event-booking-slot" data-slot="1" ' +
                  'data-starts-at="' + occ.startsAt + '" ' +
                  'data-ends-at="' + occ.endsAt + '" ' +
                  'data-date-key="' + dateKey + '" ' +
                  'data-time-label="' + time + '" ' +
                  'data-available="' + (occ.available || 0) + '" ' +
                  'data-available-by-category="' + availableByCategory + '" ' +
                  'style="' +
                    'padding:12px;' +
                    'border:2px solid ' + (disabled ? '#cbd5e1' : (isFull ? '#f59e0b' : '#4f46e5')) + ';' +
                    'border-radius:10px;' +
                    'background:' + (disabled ? '#f1f5f9' : 'white') + ';' +
                    'color:' + (disabled ? '#94a3b8' : '#1e293b') + ';' +
                    'font-size:13px;' +
                    'font-weight:600;' +
                    'cursor:' + (disabled ? 'not-allowed' : 'pointer') + ';' +
                  '" ' +
                  (disabled ? 'disabled ' : '') +
                  '>' +
                    '<div>' + time + '</div>' +
                    '<div style="font-size:11px;margin-top:4px;color:' + (disabled ? '#cbd5e1' : (isFull ? '#f59e0b' : '#10b981')) + '">' +
                      (isBlocked ? '⛔ Bloqué' : isFull ? '⌛ Liste d’attente' : '✅ ' + (occ.available || 0) + ' places') +
                    '</div>' +
                  '</button>';
        });

        html += '</div></div>';
      });
      html += '</div>';

      container.innerHTML = html;
      const slotButtons = container.querySelectorAll('button[data-slot="1"]');
      slotButtons.forEach(function(btn) {
        btn.addEventListener('click', function() {
          if (btn.disabled) return;
          const startsAt = btn.getAttribute('data-starts-at') || '';
          const endsAt = btn.getAttribute('data-ends-at') || '';
          const dateKey = btn.getAttribute('data-date-key') || '';
          const timeLabel = btn.getAttribute('data-time-label') || '';
          const available = parseInt(btn.getAttribute('data-available') || '0', 10) || 0;
          const availableByCategory = btn.getAttribute('data-available-by-category') || '';
          window['selectSlot_' + safeSlug](startsAt, endsAt, dateKey, timeLabel, available, availableByCategory);
        });
      });
    } catch (err) {
      console.error('Erreur chargement calendrier:', err);
      container.innerHTML = '<div style="text-align:center;padding:50px;color:#ef4444"><div style="font-size:48px">❌</div><div style="font-weight:600">Erreur de chargement</div></div>';
    }
  }

  function updateBookingMode() {
    const waitlistBox = document.getElementById('waitlist-box-' + safeSlug);
    const methods = document.getElementById('payment-methods-' + safeSlug);
    const warning = document.getElementById('payment-warning-' + safeSlug);
    const totalEl = document.getElementById('payment-total-' + safeSlug);
    if (!waitlistBox || !methods || !warning) return;

    const availability = getSelectedCategoryAvailability();
    if (selectedOccurrence) {
      selectedOccurrence.isWaitlist = availability != null ? availability <= 0 : selectedOccurrence.isWaitlist;
    }

    if (selectedOccurrence && selectedOccurrence.isWaitlist) {
      waitlistBox.style.display = 'block';
      methods.style.display = 'none';
      showWarning('Créneau complet : choisissez un autre créneau ou rejoignez la liste d’attente.');
      if (totalEl) totalEl.style.display = 'none';
      return;
    }

    waitlistBox.style.display = 'none';
    clearWarning();
    updatePriceSummary();
    updateCategoryAvailabilityLabel();
    if (paymentKeysLoaded) {
      updatePaymentVisibility();
    } else {
      loadPaymentKeys();
    }
  }

  window['selectSlot_' + safeSlug] = function(startsAt, endsAt, date, time, available, availableByCategoryJson) {
    let availableByCategory = null;
    try { availableByCategory = availableByCategoryJson ? JSON.parse(availableByCategoryJson) : null; } catch (e) { availableByCategory = null; }
    selectedOccurrence = { startsAt, endsAt, date, time, available, availableByCategory, isWaitlist: Number(available) <= 0 };
    const form = document.getElementById('booking-form-' + safeSlug);
    if (form) {
      form.style.display = 'block';
      form.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    const countryEl = document.getElementById('country-' + safeSlug);
    if (countryEl && !countryEl.value) {
      countryEl.value = defaultCountry || '';
    }
    updateBookingMode();
    updatePriceSummary();
  };

  function getFormData() {
    const firstNameEl = document.getElementById('first-name-' + safeSlug);
    const lastNameEl = document.getElementById('last-name-' + safeSlug);
    const emailEl = document.getElementById('email-' + safeSlug);
    const phoneEl = document.getElementById('phone-' + safeSlug);
    const addressEl = document.getElementById('address-' + safeSlug);
    const cityEl = document.getElementById('city-' + safeSlug);
    const postalEl = document.getElementById('postal-' + safeSlug);
    const countryEl = document.getElementById('country-' + safeSlug);
    const categoryEl = document.getElementById('ticket-category-' + safeSlug);
    const couponEl = document.getElementById('coupon-' + safeSlug);
    const qtyEl = document.getElementById('qty-' + safeSlug);
    const firstName = firstNameEl && firstNameEl.value ? firstNameEl.value.trim() : '';
    const lastName = lastNameEl && lastNameEl.value ? lastNameEl.value.trim() : '';
    const email = emailEl && emailEl.value ? emailEl.value.trim() : '';
    const phone = phoneEl && phoneEl.value ? phoneEl.value.trim() : '';
    const address = addressEl && addressEl.value ? addressEl.value.trim() : '';
    const city = cityEl && cityEl.value ? cityEl.value.trim() : '';
    const postalCode = postalEl && postalEl.value ? postalEl.value.trim() : '';
    const country = countryEl && countryEl.value ? countryEl.value.trim() : '';
    const ticketCategoryId = categoryEl && categoryEl.value ? categoryEl.value : '';
    const couponCode = couponEl && couponEl.value ? couponEl.value.trim() : '';
    const qty = qtyEl && qtyEl.value ? parseInt(qtyEl.value, 10) || 1 : 1;

    if (!firstName || !lastName || !email) { alert('Veuillez remplir prénom, nom et email'); return null; }
    if (!phone || !address || !city || !postalCode || !country) { alert('Veuillez remplir toutes les informations'); return null; }
    return { firstName, lastName, email, phone, address, city, postalCode, country, qty, ticketCategoryId, couponCode };
  }

  async function confirmBooking(method, transactionId) {
    if (!selectedOccurrence) return;
    if (selectedOccurrence.isWaitlist) {
      showWarning('Créneau complet : choisissez un autre créneau ou rejoignez la liste d’attente.');
      return;
    }
    const dataForm = getFormData();
    if (!dataForm) return;
    const availability = getSelectedCategoryAvailability();
    if (!selectedOccurrence.isWaitlist && availability != null && dataForm.qty > availability) {
      showWarning('Nombre de places supérieur à la disponibilité (' + availability + ').');
      return;
    }
    if (dataForm.couponCode && !resolveCoupon(dataForm.couponCode)) {
      showWarning('Code promo invalide.');
      return;
    }
    clearWarning();

    try {
      const res = await fetch(API + '/events/' + encodeURIComponent(slug) + '/book', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          occurrenceStart: selectedOccurrence.startsAt,
          occurrenceEnd: selectedOccurrence.endsAt,
          qty: dataForm.qty,
          firstName: dataForm.firstName,
          lastName: dataForm.lastName,
          email: dataForm.email,
          phone: dataForm.phone,
          address: dataForm.address,
          city: dataForm.city,
          postalCode: dataForm.postalCode,
          country: dataForm.country,
          ticketCategoryId: dataForm.ticketCategoryId || undefined,
          couponCode: dataForm.couponCode || undefined,
          paymentMethod: method || (computeTotals().totalCents > 0 ? 'manual' : 'free'),
          paymentStatus: method === 'free' ? 'PAID' : 'PAID',
          transactionId: transactionId || undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('✅ Réservation confirmée !');
        loadCalendar();
        const form = document.getElementById('booking-form-' + safeSlug);
        if (form) form.style.display = 'none';
      } else {
        alert('❌ ' + (data.message || 'Erreur de réservation'));
      }
    } catch (err) {
      console.error('Erreur réservation:', err);
      alert('❌ Erreur réseau');
    }
  }

  function loadStripeScript() {
    return new Promise(function(resolve, reject) {
      if (window.Stripe) return resolve(window.Stripe);
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = function() { resolve(window.Stripe); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  function loadPayPalScript(clientId) {
    return new Promise(function(resolve, reject) {
      if (window.paypal) return resolve(window.paypal);
      const script = document.createElement('script');
      script.src = 'https://www.paypal.com/sdk/js?client-id=' + encodeURIComponent(clientId) + '&currency=' + encodeURIComponent(currency);
      script.async = true;
      script.onload = function() { resolve(window.paypal); };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async function startStripeCheckout() {
    if (!paymentKeys.stripePublicKey) return;
    if (!selectedOccurrence) { alert('Veuillez choisir un créneau.'); return; }
    if (selectedOccurrence.isWaitlist) { alert('Ce créneau est complet.'); return; }

    const dataForm = getFormData();
    if (!dataForm) {
      alert('Veuillez remplir toutes les informations.');
      return;
    }
    if (dataForm.couponCode && !resolveCoupon(dataForm.couponCode)) {
      alert('Code promo invalide');
      return;
    }
    const totals = computeTotals();
    if (totals.totalCents <= 0) {
      confirmBooking('free');
      return;
    }

    try {
      const ensuredOwner = await ensureOwnerId();
      if (!ensuredOwner) {
        alert('Paiement indisponible : propriétaire non défini.');
        return;
      }
      const Stripe = await loadStripeScript();
      const stripe = Stripe(paymentKeys.stripePublicKey);
      const res = await fetch(API + '/events/' + encodeURIComponent(slug) + '/stripe-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ownerId: ensuredOwner,
          occurrenceStart: selectedOccurrence.startsAt,
          occurrenceEnd: selectedOccurrence.endsAt,
          qty: dataForm.qty,
          firstName: String(dataForm.firstName).trim(),
          lastName: String(dataForm.lastName).trim(),
          email: String(dataForm.email).trim(),
          phone: String(dataForm.phone).trim(),
          address: String(dataForm.address).trim(),
          city: String(dataForm.city).trim(),
          postalCode: String(dataForm.postalCode).trim(),
          country: String(dataForm.country).trim(),
          ticketCategoryId: dataForm.ticketCategoryId || undefined,
          couponCode: dataForm.couponCode || undefined,
          successUrl: window.location.origin + window.location.pathname,
          cancelUrl: window.location.origin + window.location.pathname
        })
      });
      const data = await res.json();
      if (data && data.free) {
        alert('✅ Réservation confirmée !');
        loadCalendar();
        const form = document.getElementById('booking-form-' + safeSlug);
        if (form) form.style.display = 'none';
        return;
      }
      if (!data.sessionId) {
        alert(data.message || 'Impossible de créer la session Stripe.');
        return;
      }
      await stripe.redirectToCheckout({ sessionId: data.sessionId });
    } catch (err) {
      console.error('Stripe checkout error:', err);
      alert('Erreur Stripe.');
    }
  }

  async function confirmWaitlist() {
    if (!selectedOccurrence) return;
    const dataForm = getFormData();
    if (!dataForm) return;
    if (dataForm.couponCode && !resolveCoupon(dataForm.couponCode)) {
      alert('Code promo invalide');
      return;
    }

    try {
      const res = await fetch(API + '/events/' + encodeURIComponent(slug) + '/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          occurrenceStart: selectedOccurrence.startsAt,
          occurrenceEnd: selectedOccurrence.endsAt,
          qty: dataForm.qty,
          firstName: dataForm.firstName,
          lastName: dataForm.lastName,
          email: dataForm.email,
          phone: dataForm.phone,
          address: dataForm.address,
          city: dataForm.city,
          postalCode: dataForm.postalCode,
          country: dataForm.country,
          ticketCategoryId: dataForm.ticketCategoryId || undefined,
          couponCode: dataForm.couponCode || undefined,
          returnUrl: window.location.origin + window.location.pathname
        })
      });
      const data = await res.json();
      if (data.success) {
        alert('✅ Inscription sur liste d’attente confirmée.');
        const form = document.getElementById('booking-form-' + safeSlug);
        if (form) form.style.display = 'none';
      } else {
        const message = data.message === 'slots_available'
          ? 'Des places sont disponibles. Veuillez réserver.'
          : (data.message || 'Erreur liste d’attente');
        alert('❌ ' + message);
      }
    } catch (err) {
      console.error('Erreur waitlist:', err);
      alert('❌ Erreur réseau');
    }
  }

  async function renderPayPalButtons() {
    const container = document.getElementById('paypal-button-container-' + safeSlug);
    if (!container || !paymentKeys.paypalClientId) return;
    try {
      const paypal = await loadPayPalScript(paymentKeys.paypalClientId);
      container.style.display = 'block';
      paypal.Buttons({
        style: { layout: 'vertical' },
        createOrder: function(data, actions) {
          const totals = computeTotals();
          const amount = (totals.totalCents / 100).toFixed(2);
          return actions.order.create({
            purchase_units: [{
              amount: { value: amount, currency_code: currency }
            }]
          });
        },
        onApprove: function(data, actions) {
          return actions.order.capture().then(function(details) {
            confirmBooking('paypal', details && details.id ? details.id : undefined);
          });
        },
        onError: function(err) {
          console.error('PayPal error:', err);
          alert('Erreur PayPal.');
        }
      }).render('#paypal-button-container-' + safeSlug);
    } catch (err) {
      console.error('PayPal SDK error:', err);
    }
  }

  function updatePaymentVisibility() {
    const warning = document.getElementById('payment-warning-' + safeSlug);
    const methods = document.getElementById('payment-methods-' + safeSlug);
    const freeBtn = document.getElementById('confirm-free-' + safeSlug);
    const stripeBtn = document.getElementById('confirm-stripe-' + safeSlug);
    const paypalContainer = document.getElementById('paypal-button-container-' + safeSlug);
    if (!warning || !methods || !freeBtn || !stripeBtn || !paypalContainer) return;

    const totals = computeTotals();
    updatePriceSummary();

    if (totals.totalCents <= 0) {
      methods.style.display = 'grid';
      methods.dataset.visible = 'true';
      freeBtn.style.display = 'block';
      stripeBtn.style.display = 'none';
      paypalContainer.style.display = 'none';
      warning.style.display = 'none';
      warning.dataset.visible = 'false';
      return;
    }

    if (!ownerId) {
      warning.textContent = 'Paiement indisponible : propriétaire non défini.';
      warning.style.display = 'block';
      warning.dataset.visible = 'true';
      methods.style.display = 'none';
      methods.dataset.visible = 'false';
      return;
    }

    const hasStripe = !!paymentKeys.stripePublicKey;
    const hasPaypal = !!paymentKeys.paypalClientId;
    if (!hasStripe && !hasPaypal) {
      warning.textContent = 'Aucun moyen de paiement disponible.';
      warning.style.display = 'block';
      warning.dataset.visible = 'true';
      methods.style.display = 'none';
      methods.dataset.visible = 'false';
      return;
    }

    warning.style.display = 'none';
    warning.dataset.visible = 'false';
    methods.style.display = 'grid';
    methods.dataset.visible = 'true';
    freeBtn.style.display = 'none';
    stripeBtn.style.display = hasStripe ? 'block' : 'none';
    paypalContainer.style.display = hasPaypal ? 'block' : 'none';
    if (hasPaypal) renderPayPalButtons();
  }

  async function loadPaymentKeys() {
    const warning = document.getElementById('payment-warning-' + safeSlug);
    const methods = document.getElementById('payment-methods-' + safeSlug);
    const freeBtn = document.getElementById('confirm-free-' + safeSlug);
    const stripeBtn = document.getElementById('confirm-stripe-' + safeSlug);
    const paypalContainer = document.getElementById('paypal-button-container-' + safeSlug);

    if (!warning || !methods || !freeBtn || !stripeBtn || !paypalContainer) return;
    paymentKeysLoaded = true;

    if (computeTotals().totalCents <= 0) {
      updatePaymentVisibility();
      return;
    }

    const ensuredOwner = await ensureOwnerId();
    if (!ensuredOwner) {
      warning.textContent = 'Paiement indisponible : propriétaire non défini.';
      warning.style.display = 'block';
      warning.dataset.visible = 'true';
      methods.style.display = 'none';
      methods.dataset.visible = 'false';
      return;
    }

    try {
      const res = await fetch(API + '/payment-keys/public/' + encodeURIComponent(ensuredOwner));
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const data = await res.json();
      paymentKeys = data || {};
      const hasStripe = !!paymentKeys.stripePublicKey;
      const hasPaypal = !!paymentKeys.paypalClientId;
      if (!hasStripe && !hasPaypal) {
        warning.textContent = 'Aucun moyen de paiement disponible.';
        warning.style.display = 'block';
        warning.dataset.visible = 'true';
        methods.style.display = 'none';
        methods.dataset.visible = 'false';
        return;
      }
      updatePaymentVisibility();
    } catch (err) {
      console.error('Erreur chargement paiements:', err);
      warning.textContent = 'Erreur chargement des moyens de paiement.';
      warning.style.display = 'block';
      warning.dataset.visible = 'true';
      methods.style.display = 'none';
      methods.dataset.visible = 'false';
    }
  }

  const freeBtn = document.getElementById('confirm-free-' + safeSlug);
  if (freeBtn) freeBtn.addEventListener('click', function() { confirmBooking('free'); });
  const stripeBtn = document.getElementById('confirm-stripe-' + safeSlug);
  if (stripeBtn) stripeBtn.addEventListener('click', startStripeCheckout);
  const waitlistBtn = document.getElementById('confirm-waitlist-' + safeSlug);
  if (waitlistBtn) waitlistBtn.addEventListener('click', confirmWaitlist);
  const qtyInput = document.getElementById('qty-' + safeSlug);
  if (qtyInput) qtyInput.addEventListener('input', function() { updateBookingMode(); });
  const couponInput = document.getElementById('coupon-' + safeSlug);
  if (couponInput) couponInput.addEventListener('input', function() { updateBookingMode(); });
  const categorySelect = document.getElementById('ticket-category-' + safeSlug);
  if (categorySelect) categorySelect.addEventListener('change', function() { updateBookingMode(); });

  const params = new URLSearchParams(window.location.search);
  const stripeStatus = params.get('stripe');
  const sessionId = params.get('session_id');
  if (stripeStatus === 'cancel') {
    alert('Paiement annulé.');
    const url = new URL(window.location.href);
    url.searchParams.delete('stripe');
    url.searchParams.delete('session_id');
    window.history.replaceState({}, '', url.toString());
  }
  if (stripeStatus === 'success' && sessionId) {
    ensureOwnerId().then(function(resolvedOwner) {
      if (!resolvedOwner) return;
      fetch(API + '/events/' + encodeURIComponent(slug) + '/stripe-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ownerId: resolvedOwner, sessionId })
      }).then(function(res) { return res.json(); })
        .then(function(data) {
          if (data && data.success) {
            alert('✅ Paiement confirmé.');
            loadCalendar();
          } else {
            alert('❌ ' + (data && data.message ? data.message : 'Erreur confirmation paiement'));
          }
        })
        .catch(function(err) {
          console.error(err);
        })
        .finally(function() {
          const url = new URL(window.location.href);
          url.searchParams.delete('stripe');
          url.searchParams.delete('session_id');
          window.history.replaceState({}, '', url.toString());
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      loadCalendar();
      loadPaymentKeys();
    });
  } else {
    loadCalendar();
    loadPaymentKeys();
  }
})();
</script>
  `.trim();
}

const EventBookingPlugin: PluginInterface = {
  metadata: {
    id: 'event-booking',
    name: 'Réservation Événement',
    description: 'Système de réservation pour concerts, cours ou RDV.',
    icon: '🎟️',
    category: 'commerce',
    version: '2.0.0',
    author: 'Flexihub',
    tags: ['booking','event','appointment']
  },

  // ✅ DOCUMENTATION IA
  // ⚠️ Checklist: mettre à jour documentationForAI à chaque évolution fonctionnelle.
  documentationForAI: {
    description: "Système complet de réservation et billetterie (événements, cours récurrents, RDV) avec paiements et notifications.",
    usage: "Utilise ce plugin pour proposer des créneaux réservables, paiements (Stripe/PayPal), coupons et tickets.",
    configSchema: {
      title: "Nom de l'événement",
      slug: "URL unique de l'événement",
      description: "Détails de l'événement",
      currency: "Devise (EUR, USD, ...)",
      priceCents: "Prix en centimes (5000 = 50€)",
      ticketCategories: "Catégories de billets (Standard/VIP/Enfant)",
      coupons: "Codes promo % ou montant fixe",
      paymentMethods: "Stripe / PayPal si clés présentes",
      termsCgv: "CGU/CGV (HTML)",
      termsRgpd: "RGPD (HTML)"
    },
    exampleConfig: {
      title: "Concert de Jazz",
      priceCents: 2000,
      slug: "concert-jazz-paris",
      currency: "EUR",
      ticketCategories: [{ name: "Standard", priceCents: 2000 }],
      coupons: [{ code: "WELCOME", type: "percent", value: 10 }],
      termsCgv: "<p>Conditions générales...</p>",
      termsRgpd: "<p>Politique RGPD...</p>"
    }
  },

  defaultConfig,
  ConfigComponent: EventBookingConfigComponent,
  PreviewComponent: EventBookingRenderer,

  // ✅ Placeholder pour TinyMCE (édition avec ID unique)
  generateHTML: (config: EventBookingConfig) => {
    // 1. Gestion ID unique
    const instanceId = config.id || `booking-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const configWithId = { ...config, id: instanceId };

    const typeEmoji =
      config.eventType === 'ONE_SHOT' ? '🎵' :
      config.eventType === 'RECURRING' ? '📚' : '✂️';

    const typeLabel =
      config.eventType === 'ONE_SHOT' ? 'Événement unique' :
      config.eventType === 'RECURRING' ? 'Cours récurrent' : 'Prise de RDV';

    const title = config.title?.trim() || 'Réservation Événement';
    const description =
      (config.description?.trim()) ||
      `${typeEmoji} ${typeLabel} • ${(config.occurrences?.length || 0)} date(s) • ${(config.priceCents/100).toFixed(2)} ${config.currency}`;

    return generateUniformPlaceholder({
      pluginId: instanceId, // ✅ Utilisation de l'ID unique
      pluginType: 'event-booking',
      category: 'commerce',
      title,
      description,
      icon: '🎟️',
      config: configWithId
    });
  },

  // ✅ Widget dynamique pour publication
  generatePublicHTML: (config: EventBookingConfig) => buildPublicWidget(config)
};

export default EventBookingPlugin;
